<template>
  <div class="billing">
    <h2
      v-text="$t('account.billing.title')"
      class="billing__title tw-text-xl tw-border-0 tw-border-solid tw-mb-12 tw-border-b tw-border-gray-900"
    />

    <form
      class="billing__form tw-w-full"
      :disabled="hasPendingRequest"
      @submit.prevent="submitted"
    >
      <ValidationObserver
        ref="observer"
      >
        <template slot-scope="{ dirty }">
          <ValidationProvider
            ref="email-provider"
            :name="$t('account.billing.fields.email')"
            rules="email"
            slim
          >
            <template
              slot-scope="{ invalid, validated, errors }"
            >
              <ctk-input-text
                id="email"
                v-model="formData.email"
                :label="$t('account.billing.fields.email') | capitalize"
                :disabled="hasPendingRequest"
                :error="invalid && validated"
                :hint="errors[0]"
                :loader="$wait.is('fetching company')"
                data-test="billing-email-field"
                type="email"
                name="email"
                class="tw-mb-3"
                autocomplete="email"
                clearable
              />
            </template>
          </ValidationProvider>

          <!-- Address -->
          <fieldset
            class="billing__form__address pt-2"
          >
            <legend class="tw-hidden">
              {{ $t('account.billing.titles.address') }}
            </legend>

            <ctk-input-text
              id="street_name"
              v-model="formData.address.street_name"
              :label="$t('account.billing.fields.street_name') | capitalize"
              :disabled="true"
              :loader="$wait.is('fetching company')"
              data-test="street-field"
              type="text"
              name="street_name"
              class="tw-mb-2"
              readonly
              required
            />

            <div class="billing__form__address__aside tw-flex tw-flex-col 2sm:tw-flex-row tw-mb-2">
              <ctk-input-text
                id="postal_code"
                v-model="formData.address.postal_code"
                :label="$t('app.labels.postal_code')"
                :disabled="true"
                :loader="$wait.is('fetching company')"
                data-test="postal-code-field"
                type="text"
                name="postal_code"
                class="tw-w-full 2sm:tw-w-1/3 2sm:tw-mr-2 tw-mb-2 2sm:tw-mb-0"
                readonly
                required
              />
              <ctk-input-text
                id="city"
                v-model="formData.address.city"
                :label="$t('app.labels.city')"
                :disabled="true"
                :loader="$wait.is('fetching company')"
                data-test="city-field"
                type="text"
                name="city"
                class="tw-w-full 2sm:tw-w-1/3 2sm:tw-mr-2 tw-mb-2 2sm:tw-mb-0"
                readonly
                required
              />

              <country-selector
                id="country"
                v-model="formData.address.country"
                :label="$t('app.labels.country')"
                :disabled="true"
                :items="countries"
                :data-vv-as="$t('app.fields.country')"
                :loader="$wait.is('fetching company')"
                :preferred-countries="true"
                data-test="country-field"
                color="#287696"
                has-name
                type="text"
                name="country"
                class="2sm:tw-w-1/3"
                readonly
                required
              />
            </div>
          </fieldset>

          <ValidationProvider
            v-if="hasFrenchVatNumber"
            ref="electronic-invoicing-address-provider"
            :name="$t('account.billing.fields.electronic_invoicing_address')"
            :rules="electronicInvoicingAddressRules"
            :custom-messages="{ regex: $t('account.billing.validation.electronic_invoicing_address_invalid') }"
            slim
          >
            <template
              slot-scope="{ errors, invalid, validated }"
            >
              <div class="tw-flex tw-items-start">
                <ctk-input-text
                  id="electronic_invoicing_address"
                  v-model="formData.electronic_invoicing_address"
                  :label="$t('account.billing.fields.electronic_invoicing_address') | capitalize"
                  :disabled="hasPendingRequest || isElectronicInvoicingAddressLocked"
                  :error="invalid && validated"
                  :hint="errors[0]"
                  :loader="$wait.is('fetching company')"
                  data-test="electronic-invoicing-address-field"
                  type="text"
                  name="electronic_invoicing_address"
                  class="tw-mb-3 tw-flex-1"
                  :ignore-optional="true"
                />
                <img
                  id="electronic-invoicing-address-info"
                  src="@/assets/img/icons/info_inverted.svg"
                  alt=""
                  width="16"
                  height="16"
                  class="tw-ml-2 tw-mt-3 tw-cursor-pointer tw-flex-shrink-0"
                >
                <b-tooltip
                  target="electronic-invoicing-address-info"
                  triggers="click"
                >
                  {{ $t('account.billing.tooltips.electronic_invoicing_address_info') }}
                </b-tooltip>
              </div>
            </template>
          </ValidationProvider>

          <p
            class="billing__form__address-explanation tw-italic tw-leading-tight"
            data-test="address-explanation"
          >
            {{ $t('account.billing.paragraphs.address') }}
          </p>

          <div class="billing__form__buttons tw-flex tw-justify-between tw-mt-10">
            <ui-button
              :disabled="!dirty || hasPendingRequest"
              variant="link"
              type="button"
              data-test="cancel-button"
              @click="reset"
            >
              {{ $t('account.buttons.cancel') }}
            </ui-button>
            <ui-button
              :loading="$wait.is('updating billing infos')"
              :disabled="hasPendingRequest"
              class="billing__form__buttons__save tw-mr-2"
              variant="primary"
              type="submit"
              data-test="save-button"
            >
              {{ $t('account.buttons.save') }}
            </ui-button>
          </div>
        </template>
      </ValidationObserver>
    </form>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import store from '@/store'
  import { showToaster } from '@/services/Toaster'
  import { Company } from '@/resources'
  import usePhoneCodeCountries from '@/composables/usePhoneCodeCountries'

  import CountrySelector from '@/components/CtkPhoneNumber/_subs/CountrySelector'
  import CtkInputText from '@/components/CtkInputs/CtkInputText'

  /**
   * @module view - billing
   */
  export default {
    name: 'Billing',
    components: {
      CtkInputText,
      CountrySelector
    },
    metaInfo () {
      return {
        title: this.$t('account.billing.title')
      }
    },
    beforeRouteEnter (to, from, next) {
      store.dispatch('setAppReady', true)

      next()
    },
    data () {
      return {
        tempEmail: null,
        tempElectronicInvoicingAddress: null,
        address: {},
        formData: {
          email: null,
          electronic_invoicing_address: null,
          address: {
            street_name: null,
            postal_code: null,
            city: null,
            country: null
          }
        }
      }
    },
    mounted () {
      this.reset()

      /**
       * Fetch company informations
       * (do ignore if we're in a test environment; causing http issues
       * during the unit tests)
       */
      if (process.env.NODE_ENV !== 'test') {
        this.fetchCompany()
      }
    },
    computed: {
      ...mapGetters('auth', [
        'getUserInfos',
        'getVatNumber',
        'hasFrenchVatNumber'
      ]),
      countries () {
        const { countries } = usePhoneCodeCountries()
        return countries
      },
      /**
       * Returns true if there are any pending requests
       * @function hasPendingRequest
       * @returns {boolean}
       */
      hasPendingRequest () {
        return this.$wait.is('updating billing infos') || this.$wait.is('fetching company')
      },
      /**
       * Returns true if electronic invoicing address is already set (write-once)
       * @function isElectronicInvoicingAddressLocked
       * @returns {boolean}
       */
      isElectronicInvoicingAddressLocked () {
        return !!this.tempElectronicInvoicingAddress
      },
      /**
       * Validation rules for the electronic invoicing address field.
       * Ensures the address starts with the company's SIREN (digits 5-13 of the VAT number).
       * @function electronicInvoicingAddressRules
       * @returns {object}
       */
      electronicInvoicingAddressRules () {
        if (this.isElectronicInvoicingAddressLocked) {
          return {}
        }
        const siren = this.getVatNumber.slice(4, 13)
        return { regex: new RegExp(`^${siren}(_[-.\\w]{1,115})?$`) }
      }
    },
    methods: {
      ...mapActions('auth', [
        'retrieveCompany'
      ]),
      /**
       * Fetch the company informations
       * @function fetchCompany
       */
      fetchCompany () {
        this.$wait.start('fetching company')
        this.retrieveCompany()
          .then(({ data }) => {
            const { billing } = data
            this.formData.email = billing.email
            this.tempEmail = billing.email

            this.formData.electronic_invoicing_address = billing.electronic_invoicing_address || null
            this.tempElectronicInvoicingAddress = billing.electronic_invoicing_address || null

            if (billing.address) {
              this.address = {
                street_name: billing.address.street_name,
                city: billing.address.city,
                postal_code: billing.address.postal_code,
                country: billing.address.country
              }
              this.formData.address = Object.assign({}, this.address)
            }
          })
          .catch(() => {})
          .finally(() => {
            this.$wait.end('fetching company')
          })
      },
      submitted () {
        if (this.$wait.is('updating billing infos')) {
          return
        }

        this.$refs.observer.validate()
          .then(async valid => {
            if (!valid) {
              return
            }

            const companyUuid = this.getUserInfos.companies[0].uuid

            this.$wait.start('updating billing infos')

            try {
              /**
               * Update or delete the company email
               */
              if (this.formData.email) {
                await Company.billingEmail({ cid: companyUuid }, {
                  email: this.formData.email
                })
              } else {
                await Company.deleteBillingEmail({ cid: companyUuid })
              }

              /**
               * Set electronic invoicing address (only if provided and not yet locked)
               */
              if (this.hasFrenchVatNumber && this.formData.electronic_invoicing_address && !this.isElectronicInvoicingAddressLocked) {
                await Company.electronicInvoicingAddress({ cid: companyUuid }, {
                  electronic_invoicing_address: this.formData.electronic_invoicing_address
                })
              }

              if (this.formData.electronic_invoicing_address && !this.isElectronicInvoicingAddressLocked) {
                this.tempElectronicInvoicingAddress = this.formData.electronic_invoicing_address
              }
              this.tempEmail = this.formData.email

              showToaster(
                this,
                this.$t('account.billing.paragraphs.updated'), {
                  type: 'success',
                  position: 'bottom-right'
                }
              )
            } catch (err) {
              if (!err.response) return

              const message = (err.response.data && err.response.data.error && err.response.data.error.title) ||
                this.$t('account.paragraphs.generic_error')

              showToaster(
                this,
                message, {
                  type: 'error',
                  position: 'bottom-right'
                }
              )
            } finally {
              this.$wait.end('updating billing infos')
            }
          })
      },
      /**
       * Set the fields information according to the current user informations
       * @function reset
       */
      reset () {
        this.formData = {
          email: this.tempEmail,
          electronic_invoicing_address: this.tempElectronicInvoicingAddress,
          address: Object.assign({
            street_name: null,
            city: null,
            postal_code: null,
            country: null
          }, this.address)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .billing {
    &__title {
      @media only screen and (max-width: 857px) {
        display: none;
      }
    }
  }
</style>
